"use client";

import posthog from "posthog-js";
import { useSyncExternalStore } from "react";
import { type ConvexReactClient, useConvex } from "convex/react";
import { resolveTelemetryRelease } from "@/lib/telemetry-runtime";

export type RefreshOutcome = {
  token: string | null;
  failure: unknown;
  attempts: number;
};

type SessionEnd = RefreshOutcome & { socketWasConnected: boolean };

const FAILURE_MESSAGE_LIMIT = 300;

const endedClients = new WeakSet<ConvexReactClient>();
const listeners = new Set<() => void>();

function describeFailure(failure: unknown): { name: string; message: string } {
  if (failure === undefined) {
    return { name: "NoToken", message: "WorkOS refresh returned no token" };
  }
  if (failure instanceof Error) {
    return { name: failure.name, message: failure.message.slice(0, FAILURE_MESSAGE_LIMIT) };
  }
  return { name: typeof failure, message: String(failure).slice(0, FAILURE_MESSAGE_LIMIT) };
}

function recordSessionEnd(end: SessionEnd): void {
  const failure = describeFailure(end.failure);
  posthog.capture("protected_session_ended", {
    refresh_error_name: failure.name,
    refresh_error_message: failure.message,
    refresh_attempts: end.attempts,
    convex_auth_path: end.socketWasConnected ? "scheduled_refresh" : "token_rejected",
    release: resolveTelemetryRelease(),
    pathname: window.location.pathname,
  });
}

export function endProtectedConvexSession(
  client: ConvexReactClient,
  end: SessionEnd,
): void {
  if (endedClients.has(client)) return;
  endedClients.add(client);
  recordSessionEnd(end);
  for (const listener of listeners) listener();
  void client.close();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useConvexSessionEnded(): boolean {
  const client = useConvex();
  return useSyncExternalStore(
    subscribe,
    () => endedClients.has(client),
    () => false,
  );
}
